import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { PARTNER_CONSOLIDATED_REPORTS_FLAG } from 'tio-employee/types/partner-reports';
import { resolvePartnerPersonas } from 'tio-employee/utils/partner-dashboard/personas';
import {
  widgetsFor,
  type DashboardContext,
  type RegisteredWidget,
} from 'tio-employee/utils/partner-dashboard/registry';
import { PARTNER_DASHBOARD_WIDGETS } from 'tio-employee/utils/partner-dashboard/widgets';
import type FeaturesService from 'tio-common/services/features';
import type SessionContextService from 'tio-employee/services/session-context';
import type PartnerService from 'tio-employee/services/partner';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type RouterService from '@ember/routing/router-service';

/** One visible widget and its in-flight data, resolved by the widget frame. */
export interface LoadedWidget {
  definition: RegisteredWidget;
  data: Promise<unknown>;
}

export interface PartnerDashboardModel {
  context: DashboardContext;
  widgets: LoadedWidget[];
}

export default class AuthenticatedAdminIndexRoute extends Route {
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare partner: PartnerService;
  @service declare router: RouterService;
  @service declare betaExperience: BetaExperienceService;

  #context: DashboardContext | undefined;

  /** The widgets this route can show; tests swap in their own list. */
  registry: readonly RegisteredWidget[] = PARTNER_DASHBOARD_WIDGETS;

  async beforeModel() {
    if (!this.sessionContext.isLoaded) {
      try {
        await this.sessionContext.load();
      } catch (e) {
        console.error('Could not load session context:', e);
      }
    }
    // Personas, not the legacy role claims behind isParticipantOnly, decide who gets the dashboard.
    this.#context = this.#dashboardContext();
    if (this.#context) return;

    if (this.sessionContext.isParticipantOnly) {
      this.router.transitionTo('authenticated.dashboard');
      return;
    }
    if (this.sessionContext.isTasRole || this.partner.isTaOnly) {
      this.router.transitionTo('authenticated.admin.tuition-assistance.index');
      return;
    }
    if (this.sessionContext.isPslfAdmin) {
      this.router.transitionTo('authenticated.admin.pslf.forms');
      return;
    }
    this.router.transitionTo(
      this.features.isEnabled(PARTNER_CONSOLIDATED_REPORTS_FLAG)
        ? 'authenticated.admin.reporting.index'
        : 'authenticated.admin.reports.index'
    );
  }

  /**
   * Starts every visible widget's load without awaiting it, so the page paints
   * first and each widget resolves on its own.
   */
  model(): PartnerDashboardModel | undefined {
    const context = this.#context;
    if (!context) return undefined;

    const widgets = widgetsFor(this.registry, context).map((definition) => {
      // A load that throws synchronously still lands in its widget's error state.
      const data = Promise.resolve().then(() => definition.load(context));
      // Observed now so a rejection before the frame renders isn't reported as unhandled; the frame still sees it.
      data.catch(() => {});
      return { definition, data };
    });
    return { context, widgets };
  }

  /** The dashboard context when beta is on and the viewer holds a partner persona; otherwise undefined. */
  #dashboardContext(): DashboardContext | undefined {
    if (!this.betaExperience.isEnabled) return undefined;

    const companyId = this.sessionContext.currentCompany?.id ?? '';
    const personas = resolvePartnerPersonas(
      this.sessionContext.currentEmployee?.jurisdictiveRelationships,
      companyId
    );
    return personas.size ? { personas, companyId } : undefined;
  }
}
